import { getSearchParamFilters } from '../../utils';
import {
  COLUMN_GROUPS,
  CRITICALITY_LABEL_KEYS,
  EXPORT_COLUMNS_STORAGE_KEY,
  EXPORT_SCOPE,
  KEY_COLUMN,
  REFORMATTED_FILTER,
  VIEW_IGNORED_PARAMS
} from '../../machineCsv.constants';

export const columnLabel = (t, key) => t(`assets.csv.columns.${key}`, { defaultValue: key });

// Every column but Machine ID, which is always exported.
export const selectableKeys = columns =>
  (columns || []).filter(c => c.key !== KEY_COLUMN).map(c => c.key);

// Whether every selectable column is ticked, and whether any is.
export const columnSelection = (columns, selected) => {
  const keys = selectableKeys(columns);
  const ticked = keys.filter(key => selected.includes(key)).length;
  return { all: keys.length > 0 && ticked === keys.length, some: ticked > 0 };
};

// The editable columns and Name, which is what a bulk rename needs.
export const defaultExportColumns = columns =>
  (columns || []).filter(c => c.group === COLUMN_GROUPS.EDITABLE).map(c => c.key);

// The header a CSV must carry for this column: always the backend's English
// label, whatever language the dialog is shown in.
export const csvHeaderOf = column => (column && column.label) || '';

// The remembered column choice is kept per user and per site.
export const exportColumnsStorageKey = (userId, accountId) =>
  [EXPORT_COLUMNS_STORAGE_KEY, userId || 'user', accountId || 'site'].join('.');

export const readStoredColumns = (available, storageKey) => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(storageKey));
    if (!Array.isArray(stored)) return null;
    const keys = (available || []).map(c => c.key);
    const known = stored.filter(key => keys.includes(key));
    return known.length ? known : null;
  } catch (e) {
    return null;
  }
};

export const storeColumns = (keys, storageKey) => {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(keys));
  } catch (e) {
    // A private window or blocked storage: the choice is simply not remembered.
  }
};

export const hasActiveView = (searchParams) => {
  if (!searchParams) return false;
  if (searchParams.searchKey) return true;
  const applied = {};
  Object.keys(searchParams).forEach((key) => {
    if (!VIEW_IGNORED_PARAMS.includes(key)) applied[key] = searchParams[key];
  });
  return getSearchParamFilters(applied).length > 0;
};

// The same filters, search and sort the Assets list requests, so "Current
// view" is exactly what the page shows.
export const buildExportParams = (machinesState, columns, scope) => {
  const params = { columns: columns.filter(key => key !== KEY_COLUMN) };
  const { searchParams, sorter } = machinesState || {};
  // The page's sort applies to both scopes; only the filters are the view's.
  if (sorter && sorter.sorter && sorter.sorter.order) {
    params.order_by = { field: sorter.sorter.name, direction: sorter.sorter.order };
  }
  if (scope === EXPORT_SCOPE.ALL) return params;

  if (searchParams) {
    if (searchParams.searchKey) params.search_key = searchParams.searchKey;
    params.filters = getSearchParamFilters(searchParams);
  }
  return params;
};

// filename="x.csv" from a Content-Disposition header, or null.
export const fileNameFromHeader = (header) => {
  const match = /filename="?([^";]+)"?/i.exec(header || '');
  return match ? match[1].trim() : null;
};

export const bytesToMb = bytes => Math.round((bytes || 0) / (1024 * 1024));

// The CSV headers an import can change, from the catalog: "Name, Unique ID".
export const editableHeaders = columns =>
  (columns || []).filter(column => column.editable).map(csvHeaderOf).join(', ');

export const isCsvFile = file => /\.csv$/i.test(file.name);

export const changeKey = change => `${change.machine_id}-${change.field}`;

// filter is null (all), a field key, or REFORMATTED_FILTER.
export const changeMatchesFilter = (change, filter) => {
  if (!filter) return true;
  if (filter === REFORMATTED_FILTER) return Boolean(change.reformatted);
  return change.field === filter;
};

export const problemKey = (problem, index) =>
  `${problem.row}-${problem.machine_id}-${problem.column}-${problem.code}-${index}`;

export const exportErrorMessage = (t, error) => {
  if (error && error.error_code === 'unknown_columns') {
    return t('assets.csv.export.unknownColumns', {
      columns: ((error.params && error.params.columns) || []).join(', '),
      defaultValue: 'These columns are no longer available: {{columns}}. Reopen the export and pick again.'
    });
  }
  return t('assets.csv.export.failed', {
    defaultValue: 'The export could not be created. Please try again.'
  });
};

const pad = value => String(value).padStart(2, '0');

export const buildExportFileName = (siteName, date) => {
  const slug = String(siteName || 'site').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'site';
  const day = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  return `${slug}-assets-${day}.csv`;
};

// A file-level problem arrives as { error_code, params }; anything else is a
// transport failure worded generically.
export const readFileError = (error) => {
  if (error && error.error_code) {
    return { code: error.error_code, params: error.params || {} };
  }
  return { code: 'request_failed', params: {}, message: error && error.message };
};

export const fileErrorMessage = (t, fileError, extra) => t(`assets.csv.fileErrors.${fileError.code}`, {
  ...(extra || {}),
  ...fileError.params,
  defaultValue: t('assets.csv.fileErrors.request_failed', {
    defaultValue: 'The file could not be checked. Please try again.'
  })
});

const listRows = rows => (Array.isArray(rows) ? rows.join(', ') : rows);

const orBlank = value => (value === null || value === undefined ? '' : value);

// Every placeholder a problem message may use gets a value, so a missing
// param reads as a gap and never as a literal {{placeholder}}.
export const problemMessage = (t, problem) => {
  const params = problem.params || {};
  return t(`assets.csv.problemCodes.${problem.code}`, {
    value: '',
    length: '',
    max: '',
    ...params,
    rows: orBlank(listRows(params.rows)),
    column: problem.column ? columnLabel(t, problem.column) : '',
    machineId: orBlank(problem.machine_id),
    otherId: orBlank(params.other_id),
    otherName: orBlank(params.other_name),
    machineName: orBlank(params.machine_name),
    defaultValue: problem.code
  });
};

export const criticalityLabel = (t, value) => {
  const key = CRITICALITY_LABEL_KEYS[value];
  return key ? t(`assets.options.criticality.${key}`) : String(value);
};

export const isEmptyValue = value => value === null || value === undefined || value === '';

export const formatValue = (t, field, value) => {
  if (isEmptyValue(value)) return '';
  if (field === 'criticality') return criticalityLabel(t, value);
  return String(value);
};

export const countByField = (changes) => {
  const counts = {};
  (changes || []).forEach((change) => {
    counts[change.field] = (counts[change.field] || 0) + 1;
  });
  return counts;
};

export const getImportStep = (importState) => {
  if (importState.applied) return 'done';
  if (importState.applyProblems && importState.applyProblems.length) return 'problems';
  const { preview } = importState;
  if (preview && preview.problems && preview.problems.length) return 'problems';
  if (preview) return 'preview';
  return 'upload';
};

// A cell a spreadsheet would read as a formula gets a leading quote, as the
// backend export does: a problem message can start with a machine name.
const FORMULA_START = /^[=+\-@\t\r]/;

const csvCell = (value) => {
  const raw = value === null || value === undefined ? '' : String(value);
  const text = typeof value === 'string' && FORMULA_START.test(raw) ? `'${raw}` : raw;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

// Every change of a preview as a CSV, so a preview longer than the table can
// still be read in full before it is applied.
export const changesToCsv = (t, changes) => {
  const header = [
    columnLabel(t, KEY_COLUMN),
    t('assets.csv.preview.machine', { defaultValue: 'Machine' }),
    t('assets.csv.preview.field', { defaultValue: 'Field' }),
    t('assets.csv.preview.now', { defaultValue: 'Now' }),
    t('assets.csv.preview.after', { defaultValue: 'After import' }),
    t('assets.csv.preview.reformatted', { defaultValue: 'Check' })
  ];
  const yes = t('common.yes', { defaultValue: 'Yes' });
  const lines = [header.map(csvCell).join(',')];
  changes.forEach((change) => {
    lines.push([
      change.machine_id,
      change.machine_name,
      columnLabel(t, change.field),
      formatValue(t, change.field, change.old),
      formatValue(t, change.field, change.new),
      change.reformatted ? yes : ''
    ].map(csvCell).join(','));
  });
  return `\ufeff${lines.join('\r\n')}\r\n`;
};

// The problems as a CSV the user can keep beside the spreadsheet they fix.
// Problems from an apply have no spreadsheet row, so that column is left out.
export const problemsToCsv = (t, problems, afterApply) => {
  const withRow = cells => (afterApply ? cells.slice(1) : cells);
  const header = withRow([
    t('assets.csv.problemsTable.row', { defaultValue: 'Row' }),
    columnLabel(t, KEY_COLUMN),
    t('assets.csv.problemsTable.column', { defaultValue: 'Column' }),
    t('assets.csv.problemsTable.problem', { defaultValue: 'Problem' })
  ]);
  const lines = [header.map(csvCell).join(',')];
  problems.forEach((problem) => {
    lines.push(withRow([
      problem.row,
      problem.machine_id,
      columnLabel(t, problem.column),
      problemMessage(t, problem)
    ]).map(csvCell).join(','));
  });
  return `\ufeff${lines.join('\r\n')}\r\n`;
};
